/**
 * HUD — the readout layer.
 *
 * Design rule taken from the genre: the player's eyes never leave the
 * playfield. Everything here must be readable PERIPHERALLY — carried by colour,
 * position and motion rather than by text you have to focus on to parse.
 * Numbers are for after the run; shape and colour are for during it.
 *
 * Three elements do real work:
 *   WEAPON STATE  — the pod mechanic made legible. Which weapon is loaded, and
 *                   how well it performs against what is currently on screen.
 *   BOSS PANEL    — per-component health. A destructible boss the player cannot
 *                   read is just a health sponge; this is what turns "shoot the
 *                   dish first" from a secret into a strategy.
 *   MEDAL CHAIN   — the scoring loop, visible.
 */

import type { WeaponId } from '../game/combat';

type BossPart = { id: string; frac: number; alive: boolean };

type HudState = {
  score: number;
  lives: number;
  bombs: number;
  medals: number;
  shield: number;
  elapsed: number;
  progress: number;
  objective: string;
  failed: boolean;
  complete: boolean;
  focused: boolean;
  weapon: WeaponId;
  /** 0..1 effectiveness of the current weapon against what is on screen. */
  weaponEffect: number;
  boss: { name: string; integrity: number; parts: BossPart[] } | null;
};

const WEAPON_COPY: Record<string, { label: string; hint: string; css: string }> = {
  HAILSTORM: { label: 'HAILSTORM', hint: 'SPREAD — CLOSE IN', css: 'hailstorm' },
  ARCLIGHT: { label: 'ARCLIGHT', hint: 'LANCE — RANGE / ARMOUR', css: 'arclight' },
};

export class Hud {
  private readonly score = this.get('#score-value');
  private readonly lives = this.get('#lives-value');
  private readonly bombs = this.get('#bombs-value');
  private readonly medals = this.get('#medal-value');
  private readonly shield = this.get('#shield-value');
  private readonly crossfire = this.get('#crossfire-callout');
  private readonly crossfireCount = this.get('#crossfire-count');
  private readonly timer = this.get('#timer-value');
  private readonly objective = this.get('#objective-line');
  private readonly progress = this.get('#stage-progress-fill');
  private readonly trim = this.get('#trim-state');
  private readonly overlay = this.get('#state-overlay');
  private readonly overlayTitle = this.get('#state-title');
  private readonly overlayCopy = this.get('#state-copy');

  private readonly weaponState = this.get('#weapon-state');
  private readonly weaponName = this.get('#weapon-name');
  private readonly weaponHint = this.get('#weapon-hint');
  private readonly weaponBars = Array.from(
    document.querySelectorAll<HTMLElement>('#weapon-bars i'),
  );

  private readonly bossPanel = this.get('#boss-panel');
  private readonly bossName = this.get('#boss-name');
  private readonly bossFill = this.get('#boss-integrity-fill');
  private readonly bossParts = this.get('#boss-parts');

  private lastWeapon: WeaponId | null = null;
  private lastMedals = 0;
  private partNodes: HTMLElement[] = [];

  /**
   * Announce a crossfire chain. The player set this up by moving, so it needs
   * acknowledging — otherwise the 1.5x bounty is invisible and the tactic
   * never gets learned.
   */
  announceCrossfire(kills: number, score: number): void {
    if (kills <= 0) return;
    this.crossfireCount.textContent = `x${kills}  +${score.toLocaleString()}`;
    this.crossfire.classList.remove('fire');
    void this.crossfire.offsetWidth; // restart the animation
    this.crossfire.classList.add('fire');
  }

  update(state: HudState): void {
    this.score.textContent = state.score.toString().padStart(7, '0');
    this.lives.textContent = '◆'.repeat(Math.max(0, state.lives));
    this.bombs.textContent = '●'.repeat(Math.max(0, state.bombs));

    // Medal counter pops on collection so the chase reads without a glance.
    this.medals.textContent = state.medals.toString();
    // Shield reads as a hard buffer count, dashes when empty.
    this.shield.textContent = state.shield > 0 ? '\u25c9'.repeat(state.shield) : '\u2014';
    this.shield.style.color = state.shield > 0 ? '#9fe8c8' : 'rgba(232,230,223,0.3)';
    if (state.medals !== this.lastMedals) {
      this.medals.animate(
        [
          { transform: 'scale(1.5)', filter: 'brightness(2.2)' },
          { transform: 'scale(1)', filter: 'brightness(1)' },
        ],
        { duration: 260, easing: 'ease-out' },
      );
      this.lastMedals = state.medals;
    }

    const minutes = Math.floor(state.elapsed / 60).toString().padStart(2, '0');
    const seconds = Math.floor(state.elapsed % 60).toString().padStart(2, '0');
    this.timer.textContent = `${minutes}:${seconds}`;
    this.objective.textContent = state.objective;
    this.progress.style.transform = `scaleX(${clamp01(state.progress)})`;
    this.trim.classList.toggle('active', state.focused);

    this.updateWeapon(state);
    this.updateBoss(state);

    this.overlay.classList.toggle('visible', state.failed || state.complete);
    this.overlayTitle.textContent = state.complete ? 'STAGE CLEAR' : 'AIRFRAME LOST';
    this.overlayCopy.textContent = state.complete
      ? 'BATTERY NINE is down. The corridor is open.'
      : 'Press Enter to launch the next airframe.';
  }

  private updateWeapon(state: HudState): void {
    const copy = WEAPON_COPY[state.weapon] ?? WEAPON_COPY.HAILSTORM;
    this.weaponName.textContent = copy.label;
    this.weaponHint.textContent = copy.hint;
    this.weaponState.classList.toggle('hailstorm', copy.css === 'hailstorm');
    this.weaponState.classList.toggle('arclight', copy.css === 'arclight');

    // The swap must never go unnoticed — the player may not have meant it.
    if (this.lastWeapon !== null && this.lastWeapon !== state.weapon) {
      this.weaponState.classList.remove('swapped');
      void this.weaponState.offsetWidth; // force reflow so the anim restarts
      this.weaponState.classList.add('swapped');
    }
    this.lastWeapon = state.weapon;

    // Effectiveness bars: how well this weapon works on what is on screen NOW.
    // This is the armour matchup surfaced without a manual.
    const lit = Math.round(clamp01(state.weaponEffect) * this.weaponBars.length);
    this.weaponBars.forEach((bar, i) => bar.classList.toggle('lit', i < lit));
  }

  private updateBoss(state: HudState): void {
    const boss = state.boss;
    this.bossPanel.classList.toggle('visible', !!boss);
    this.bossPanel.setAttribute('aria-hidden', boss ? 'false' : 'true');
    if (!boss) {
      if (this.partNodes.length > 0) {
        this.partNodes = [];
        this.bossParts.replaceChildren();
      }
      return;
    }

    this.bossName.textContent = boss.name;
    this.bossFill.style.transform = `scaleX(${clamp01(boss.integrity)})`;
    // Below the 38% threshold the root is exposed — the bar goes hot to say so.
    this.bossFill.classList.toggle('exposed', boss.integrity <= 0.38);

    // Rebuild only when the component count changes; otherwise mutate in place
    // so this stays allocation-free at 60 Hz.
    if (this.partNodes.length !== boss.parts.length) {
      this.bossParts.replaceChildren();
      this.partNodes = boss.parts.map(() => {
        const wrap = document.createElement('div');
        wrap.className = 'part';
        wrap.appendChild(document.createElement('i'));
        this.bossParts.appendChild(wrap);
        return wrap;
      });
    }
    boss.parts.forEach((part, i) => {
      const node = this.partNodes[i];
      if (!node) return;
      node.classList.toggle('dead', !part.alive);
      const bar = node.firstElementChild as HTMLElement | null;
      if (bar) bar.style.transform = `scaleX(${clamp01(part.frac)})`;
    });
  }

  private get(selector: string): HTMLElement {
    const element = document.querySelector<HTMLElement>(selector);
    if (!element) throw new Error(`Missing HUD element: ${selector}`);
    return element;
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
